import React, { useEffect, useState } from 'react';
import clsx from 'clsx';

import closeIcon from './close.svg';
import styles from './Modal.module.scss';
import { createPortal } from 'react-dom';

interface ModalProps {
    open: boolean;
    size?: 'tiny' | 'small' | 'long' | 'large' | 'full' | 'full-portrait' | 'flexible';
    onClose?: () => void;
    children?: React.ReactNode;
    withoutCloseButton?: boolean;
    /** Class on the outer backdrop container. */
    className?: string;
    /** Class on the inner modal box (unlike `className`, which targets the backdrop). Lets a caller apply static layout, e.g. a fullscreen-on-mobile variant, without an inline style. */
    modalClassName?: string;
    style?: React.CSSProperties;
    closeIconStyle?: React.CSSProperties;
    persistent?: boolean;
    blurBackground?: boolean;
}

const Modal: React.FC<ModalProps> = (props) => {
    // Initialise from `open`, NOT a hardcoded `true`. With `true`, a modal
    // mounted closed (open=false) rendered visible for the first paint and was
    // only hidden once the effect below ran — a flash on mount. For a modal
    // permanently mounted at open=false (e.g. the toolbar's import
    // Replace/Append dialog), that flash is visible during a heavy editor mount
    // and looks like the dialog "popping up on open".
    const [show, setShow] = useState(props.open);
    const persistent = props.persistent || false;
    const blurBackground = props.blurBackground || false;
    const modalSize = props.size || 'small';

    useEffect(() => {
        setShow(props.open);
        if (props.open) {
            document.body.classList.add('modal-open');
            if (blurBackground) {
                document.body.style.pointerEvents = 'none';
            }
        }

        return () => {
            document.body.classList.remove('modal-open');
            if (blurBackground) {
                document.body.style.pointerEvents = '';
            }
        };
    }, [props.open, blurBackground]);

    const handleClose = (fromOverLay = false) => {
        if (persistent && fromOverLay) return;
        props.onClose && props.onClose();
        setShow(false);
    };

    const render = (
        <div
            className={clsx(props.className, styles.ModalContainer, {
                [styles.BlurBackground]: blurBackground,
            })}
            style={{ display: show ? '' : 'none', pointerEvents: 'auto' }}
            onClick={() => handleClose(true)}
        >
            <div
                className={clsx(
                    {
                        [styles.Modal]: true,
                        [styles.Tiny]: modalSize === 'tiny',
                        [styles.Small]: modalSize === 'small',
                        [styles.Long]: modalSize === 'long',
                        [styles.Large]: modalSize === 'large',
                        [styles.Full]: modalSize === 'full',
                        [styles.FullPortrait]: modalSize === 'full-portrait',
                        [styles.Flexible]: modalSize === 'flexible',
                    },
                    props.modalClassName,
                )}
                onClick={(e) => e.stopPropagation()}
                style={{ ...props?.style }}
            >
                {!props.withoutCloseButton && (
                    <img
                        className={styles.CloseIcon}
                        src={closeIcon}
                        alt="close"
                        height="10px"
                        width="10px"
                        onClick={() => handleClose()}
                        style={{ ...props?.closeIconStyle }}
                    />
                )}
                <div className={styles.Body}>{props.children}</div>
            </div>
        </div>
    );

    return createPortal(
        render,
        document.body, // This ensures the modal breaks out of the form hierarchy
    );
};

export default Modal;
